import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CheckCircle, ArrowRight, ArrowLeft, Storefront, Bank, ShieldCheck,
  Truck, Sparkle, Info,
} from "@phosphor-icons/react";

/**
 * MerchantOnboardingMockup — a public, no-auth mockup of the
 * "sign a new restaurant onto our third-party delivery umbrella"
 * wizard.  Nothing here writes to the DB — every submit is faked
 * with setTimeout so the design can be lifted verbatim into the
 * marketing / partner-portal site that lives outside this repo.
 *
 * Route: /merchant-onboarding  (no auth required)
 */
const STEPS = [
  { key: "business", label: "Business", icon: Storefront },
  { key: "banking", label: "Banking & KYC", icon: Bank },
  { key: "brands", label: "Delivery Brands", icon: Truck },
  { key: "review", label: "Review & Sign", icon: ShieldCheck },
  { key: "done", label: "Complete", icon: Sparkle },
];

export default function MerchantOnboardingMockup() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    legal_name: "", dba: "", ein: "",
    address: "", city: "", state: "", zip: "",
    phone: "", email: "", website: "",
    hours: "Mon-Sun 11am-10pm",
    cuisine: "American",
    routing: "", account: "", bank_name: "",
    officer_name: "", officer_ssn_last4: "",
    dob: "",
    brands_dd: true, brands_ue: true, brands_gh: false,
    consent: false,
  });
  const [brandStatus, setBrandStatus] = useState({ dd: null, ue: null, gh: null });

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submitToBrands = () => {
    setSubmitting(true);
    setStep(4);
    // Fake each brand's provisioning turnaround with staggered timeouts.
    const kick = (brand, delay) => setTimeout(() => {
      setBrandStatus((prev) => ({ ...prev, [brand]: "connected" }));
    }, delay);
    if (form.brands_dd) kick("dd", 1500);
    if (form.brands_ue) kick("ue", 2800);
    if (form.brands_gh) kick("gh", 4200);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Mockup notice banner */}
      <div className="bg-amber-100 dark:bg-amber-900/40 border-b border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100 text-xs px-4 py-2 flex items-center justify-center gap-2">
        <Info size={14} weight="fill" />
        <span>MOCKUP — this page is design-only. Copy the JSX into your marketing site and wire it to the platform provisioning APIs.</span>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold">C</div>
            <span className="font-heading text-2xl">Crest POS</span>
          </div>
          <h1 className="font-heading text-3xl mb-2">Get your restaurant on DoorDash, Uber &amp; Grubhub</h1>
          <p className="text-muted-foreground">One form. Three delivery brands. No sales calls. Live in ~3 business days.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = step === i;
            const done = step > i;
            return (
              <div key={s.key} className="flex-1 flex items-center">
                <div className={`flex flex-col items-center flex-1 ${active ? "text-primary" : done ? "text-green-600" : "text-muted-foreground"}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${active ? "border-primary bg-primary/10" : done ? "border-green-600 bg-green-100 dark:bg-green-950" : "border-muted"}`}>
                    {done ? <CheckCircle size={20} weight="fill" /> : <Icon size={18} />}
                  </div>
                  <span className="text-[11px] mt-1 font-medium">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-px flex-1 -mt-4 ${done ? "bg-green-600" : "bg-muted"}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        {step === 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Tell us about your restaurant</CardTitle>
              <CardDescription>Takes ~3 minutes. All fields shared across all three brands so you never re-type.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Legal Business Name</Label>
                  <Input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} placeholder="Crestwood Kitchen LLC" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Doing Business As (DBA)</Label>
                  <Input value={form.dba} onChange={(e) => set("dba", e.target.value)} placeholder="Crestwood Kitchen" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">EIN / Tax ID</Label>
                  <Input value={form.ein} onChange={(e) => set("ein", e.target.value)} placeholder="12-3456789" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cuisine Category</Label>
                  <Input value={form.cuisine} onChange={(e) => set("cuisine", e.target.value)} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Store Address</Label>
                  <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Main St" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">City</Label>
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">State</Label>
                  <Input value={form.state} onChange={(e) => set("state", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Zip</Label>
                  <Input value={form.zip} onChange={(e) => set("zip", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Business Phone</Label>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Business Email</Label>
                  <Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="owner@restaurant.com" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Hours of Operation</Label>
                  <Input value={form.hours} onChange={(e) => set("hours", e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Banking &amp; owner verification</CardTitle>
              <CardDescription>Every brand requires this for KYC and to pay you out. It's transmitted directly to their KYC processor — Crest never stores full account numbers.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Bank Name</Label>
                  <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="Chase" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Routing Number</Label>
                  <Input value={form.routing} onChange={(e) => set("routing", e.target.value)} placeholder="9 digits" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Account Number</Label>
                  <Input value={form.account} onChange={(e) => set("account", e.target.value)} type="password" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Officer / Owner Full Name</Label>
                  <Input value={form.officer_name} onChange={(e) => set("officer_name", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">SSN — Last 4</Label>
                  <Input value={form.officer_ssn_last4} onChange={(e) => set("officer_ssn_last4", e.target.value)} maxLength={4} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Officer Date of Birth</Label>
                  <Input value={form.dob} onChange={(e) => set("dob", e.target.value)} type="date" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                <ShieldCheck size={12} weight="fill" className="inline mr-1 text-green-600" />
                Transmitted over TLS 1.3 directly to each brand's KYC processor. Never persisted on Crest servers.
              </p>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Which delivery brands?</CardTitle>
              <CardDescription>Pick all that apply. You can sign more up later from the Crest POS Settings page.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: "brands_dd", label: "DoorDash Drive", desc: "Live in ~1-3 business days. Per-delivery fee (~$8-11). No commissions.", enabled: true },
                { key: "brands_ue", label: "Uber Direct", desc: "Live in ~2-5 business days. Per-delivery fee (~$7-10). Add Uber Eats Marketplace separately.", enabled: true },
                { key: "brands_gh", label: "Grubhub Connect", desc: "Live in ~5-10 business days. Grubhub takes ~15-30% of order.", enabled: true },
              ].map((b) => (
                <label key={b.key} className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition ${form[b.key] ? "border-primary bg-primary/5" : "border-muted"}`}>
                  <Checkbox checked={form[b.key]} onCheckedChange={(v) => set(b.key, !!v)} className="mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{b.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{b.desc}</p>
                  </div>
                </label>
              ))}
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Review &amp; sign</CardTitle>
              <CardDescription>You'll receive one e-signature link per brand you selected. Sign each in ~2 min per link.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border p-4 bg-muted/30 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Legal name</span><span className="font-medium">{form.legal_name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">DBA</span><span className="font-medium">{form.dba || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Address</span><span className="font-medium">{[form.address, form.city, form.state, form.zip].filter(Boolean).join(", ") || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">EIN</span><span className="font-medium">{form.ein || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Owner</span><span className="font-medium">{form.officer_name || "—"}</span></div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Brands</span>
                  <div className="flex gap-1">
                    {form.brands_dd && <Badge className="text-[10px]">DoorDash</Badge>}
                    {form.brands_ue && <Badge className="text-[10px]">Uber</Badge>}
                    {form.brands_gh && <Badge className="text-[10px]">Grubhub</Badge>}
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} className="mt-0.5" />
                <span className="text-xs text-muted-foreground">I confirm the information above is accurate and I'm authorized to sign contracts on behalf of this business. I understand each brand's Merchant Agreement will be e-signed separately in the next step.</span>
              </label>
            </CardContent>
          </Card>
        )}

        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Sparkle size={20} weight="fill" className="text-primary" />
                Provisioning in progress
              </CardTitle>
              <CardDescription>We're creating your merchant profile at each brand and emailing you their e-signature links now.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: "dd", label: "DoorDash Drive", show: form.brands_dd },
                { key: "ue", label: "Uber Direct", show: form.brands_ue },
                { key: "gh", label: "Grubhub Connect", show: form.brands_gh },
              ].filter((b) => b.show).map((b) => {
                const st = brandStatus[b.key];
                return (
                  <div key={b.key} className="flex items-center justify-between p-4 rounded-lg border">
                    <div>
                      <p className="text-sm font-medium">{b.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {st === "connected"
                          ? "Merchant profile created — check email for e-signature link"
                          : "Talking to their provisioning API…"}
                      </p>
                    </div>
                    {st === "connected" ? (
                      <Badge className="bg-green-600 hover:bg-green-600 text-xs">
                        <CheckCircle size={12} weight="fill" className="mr-1" /> Ready to sign
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs text-muted-foreground">Connecting…</span>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="pt-2 text-center text-xs text-muted-foreground">
                We'll email {form.email || "your business email"} with next steps for each brand.
              </div>
            </CardContent>
          </Card>
        )}

        {/* Nav */}
        {step < 4 && (
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={back} disabled={step === 0}>
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            {step < 3 ? (
              <Button onClick={next}>Continue <ArrowRight size={16} className="ml-1" /></Button>
            ) : (
              <Button onClick={submitToBrands} disabled={!form.consent || submitting}>
                Submit &amp; get sign links <ArrowRight size={16} className="ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
